import React, { useState } from 'react';
import './AdminDashboard.css';
import EditBusinessForm from './EditBusinessForm';

function AdminDashboard({ categories, onUpdateBusiness, onDeleteBusiness, onLogout }) {
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Get all businesses
  const allBusinesses = categories.flatMap(cat => 
    cat.businesses.map(biz => ({ ...biz, categoryName: cat.name }))
  );

  // Filter businesses
  const filtered = allBusinesses.filter(biz => {
    const matchesSearch = biz.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         biz.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || biz.categoryName === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Sort businesses
  const sorted = [...filtered].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return b.id - a.id;
      case 'oldest':
        return a.id - b.id;
      case 'name-asc':
        return a.name.localeCompare(b.name);
      case 'name-desc':
        return b.name.localeCompare(a.name);
      default:
        return 0;
    }
  });

  const handleEditClick = (business) => {
    setSelectedBusiness(business);
    setShowEditForm(true);
  };

  const handleSaveEdit = (updatedBusiness) => {
    onUpdateBusiness(updatedBusiness);
    setShowEditForm(false);
    setSelectedBusiness(null);
  };

  const handleDeleteClick = (business) => {
    if (window.confirm(`Delete "${business.name}"? This cannot be undone.`)) {
      onDeleteBusiness(business.id, business.categoryName);
    }
  };

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-title">
          <h1>📊 Admin Dashboard</h1>
          <p>Manage all businesses and content</p>
        </div>
        <button className="logout-button" onClick={onLogout}>
          🚪 Logout
        </button>
      </div>

      {/* Statistics */}
      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-label">Total Businesses</div>
          <div className="stat-value">{allBusinesses.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Categories</div>
          <div className="stat-value">{categories.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Photos</div>
          <div className="stat-value">
            {allBusinesses.reduce((sum, b) => sum + (b.photos?.length || 0), 0)}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="dashboard-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="🔍 Search by name or address..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-controls">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.name}>{cat.name}</option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="filter-select"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="name-asc">Name (A-Z)</option>
            <option value="name-desc">Name (Z-A)</option>
          </select>
        </div>
      </div>

      {/* Businesses Table */}
      <div className="businesses-container">
        <h2 className="businesses-title">
          All Businesses ({sorted.length})
        </h2>

        {sorted.length === 0 ? (
          <div className="no-businesses">
            <p>No businesses found.</p>
          </div>
        ) : (
          <div className="businesses-list">
            {sorted.map((business) => (
              <div key={business.id} className="business-row">
                {/* Photo */}
                <div className="row-photo">
                  {business.photos && business.photos.length > 0 ? (
                    <img src={business.photos[0]} alt={business.name} />
                  ) : (
                    <div className="no-photo">No photo</div>
                  )}
                </div>

                {/* Info */}
                <div className="row-info">
                  <h3>{business.name}</h3>
                  <p className="row-type">{business.type}</p>
                  <p className="row-category">
                    <span className="category-badge">{business.categoryName}</span>
                  </p>
                  <p className="row-address">📍 {business.address}</p>
                  <p className="row-phone">📞 {business.phone}</p>
                </div>

                {/* Photos Count */}
                <div className="row-photos-count">
                  <div className="count-badge">
                    📸 {business.photos?.length || 0}/5
                  </div>
                </div>

                {/* Actions */}
                <div className="row-actions">
                  <button
                    className="action-button edit"
                    onClick={() => handleEditClick(business)}
                    title="Edit business"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    className="action-button delete"
                    onClick={() => handleDeleteClick(business)}
                    title="Delete business"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Form Modal */}
      {showEditForm && selectedBusiness && (
        <EditBusinessForm
          business={selectedBusiness}
          categories={categories}
          onSave={handleSaveEdit}
          onCancel={() => {
            setShowEditForm(false);
            setSelectedBusiness(null);
          }}
        />
      )}
    </div>
  );
}

export default AdminDashboard;
